/**
 * facturaDetailHelper.js
 * Pure data-transformation functions for the FacturaDetail view.
 * No API calls, no side effects.
 */

// Enum: I=Ingreso, E=Egreso(Nota de Crédito), N=Nómina, T=Traslado, P=Pago
const TIPO_COMPROBANTE_MAP = {
    I: { label: 'FACTURA',              badgeLabel: null,              barClass: 'bar-blue',   badgeClass: null },
    E: { label: 'NOTA DE CRÉDITO',     badgeLabel: 'Nota de Crédito', barClass: 'bar-orange', badgeClass: 'nota-credito-badge' },
    N: { label: 'NÓMINA',              badgeLabel: 'Nómina',          barClass: 'bar-blue',   badgeClass: null },
    T: { label: 'TRASLADO',            badgeLabel: 'Traslado',        barClass: 'bar-blue',   badgeClass: null },
    P: { label: 'COMPLEMENTO DE PAGO', badgeLabel: 'Pago',            barClass: 'bar-blue',   badgeClass: null },
}

/**
 * Returns { label, badgeLabel, barClass, badgeClass } for a tipoComprobante value.
 * label      — heading text (e.g. "NOTA DE CRÉDITO")
 * badgeLabel — short badge text, or null when no badge is needed
 * barClass   — CSS class for the left accent bar (bar-blue / bar-orange)
 * badgeClass — CSS class for the badge, or null
 */
export const getTipoComprobanteInfo = (tipo = 'I') =>
    TIPO_COMPROBANTE_MAP[tipo] || TIPO_COMPROBANTE_MAP['I']

const STATUS_LABELS = {
    timbrada: 'Timbrada',
    cerrada: 'Cerrada',
    cancelada: 'Cancelada',
    'cancelación en proceso': 'Cancelación en proceso',
    'cancelación rechazada': 'Cancelación rechazada',
    sustituida: 'Sustituida',
    borrador: 'Borrador',
    pendiente: 'Pendiente',
    programada: 'Programada',
    fallida: 'Fallida',
}

const STATUS_COLORS = {
    timbrada: '#34c38f',
    cerrada: '#74788d',
    cancelada: '#f46a6a',
    'cancelación en proceso': '#f1b44c',
    'cancelación rechazada': '#f46a6a',
    sustituida: '#74788d',
    borrador: '#adb5bd',
    pendiente: '#50a5f1',
    programada: '#50a5f1',
    fallida: '#f1b44c',
}

/**
 * Returns { label, color } for a given factura status.
 */
export const getStatusBadge = (status = '') => ({
    label: STATUS_LABELS[status] || status,
    color: STATUS_COLORS[status] || '#74788d',
})

/**
 * Builds emisor display object from empresa + factura fields.
 */
export const buildEmisor = (empresa = {}, factura = {}) => ({
    nombre: empresa.razonSocial || empresa.nombre || '—',
    rfc: (empresa.rfc || '').toUpperCase(),
    regimenFiscal: factura.regimenFiscal || empresa.regimenFiscal || '—',
    lugarExpedicion: factura.lugarExpedicion || empresa.cp || '—',
})

/**
 * Builds receptor display object from cliente document.
 */
export const buildReceptor = (cliente = {}, factura = {}) => ({
    nombre: cliente.razonSocial || '—',
    rfc: (cliente.rfc || '').toUpperCase(),
    domicilioFiscal: cliente.cp || '—',
    regimenFiscal: cliente.regimenFiscal || '—',
    usoCFDI: factura.usoCFDI || '—',
    residenciaFiscal: cliente.residenciaFiscal || null,
})

/**
 * Normalises lineItems into a flat array of display rows.
 */
export const buildConceptos = (lineItems = []) =>
    lineItems.map((item) => ({
        descripcion: item.Descripcion || item.descripcion || '—',
        claveProdServ: item.ClaveProdServ || item.claveProdServ || '',
        claveUnidad: item.ClaveUnidad || item.claveUnidad || '',
        cantidad: parseFloat(item.Cantidad ?? item.cantidad) || 0,
        valorUnitario: parseFloat(item.ValorUnitario ?? item.valorUnitario) || 0,
        importe: parseFloat(item.Importe ?? item.importe) || 0,
        descuento: parseFloat(item.Descuento ?? item.descuento) || 0,
    }))

/**
 * Returns totales display object from factura fields.
 */
export const buildTotales = (factura = {}) => {
    const subtotal = parseFloat(factura.subtotal) || 0
    const iva = parseFloat(factura.iva) || 0
    const retenciones = parseFloat(factura.retenciones) || 0
    const descuento = parseFloat(factura.descuento) || 0
    const total = parseFloat(factura.total) || 0

    return {
        subtotal,
        iva: iva > 0 ? iva : null,
        retenciones: retenciones > 0 ? retenciones : null,
        descuento: descuento > 0 ? descuento : null,
        total,
        moneda: factura.moneda || 'MXN',
        tipoCambio:
            factura.moneda && factura.moneda !== 'MXN' && factura.moneda !== 'XXX' && factura.tipoCambio
                ? parseFloat(factura.tipoCambio) || factura.tipoCambio
                : null,
    }
}

/**
 * Returns timbre fiscal digital fields (null when not timbrada).
 */
export const buildTimbre = (factura = {}) => {
    if (!factura.uuid) return null

    return {
        uuid: factura.uuid,
        fechaTimbrado: factura.fechaTimbrado || null,
        noCertificado: factura.version ? factura.version : null,
        cadenaOriginalTFD: factura.cadenaOriginalTFD || null,
        cadenaOriginal: factura.cadenaOriginal || null,
    }
}

/**
 * Returns cfdiRelacionados rows or empty array.
 */
export const buildCfdiRelacionados = (factura = {}) =>
    Array.isArray(factura.cfdiRelacionados) ? factura.cfdiRelacionados : []
